/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { classnames } from 'Utils/classnames/classnames.utils';
import { get } from 'lodash';
import moment from 'moment';
import { useFormikContext } from 'formik';
import React, { useState, useEffect, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { setTravelCompanionField } from 'Components/widgets/contact-info/ContactInfoUtils';
import { t } from 'Src/i18n';
import {
  selectTravelCompanion,
  unselectTravelCompanion,
} from 'Store/booking/travel-companion/travel-companion.action';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './TravelCompanionBlock.themes';
type Props = {
  travelCompanion: any;
  isChild: boolean;
  position: number;
  parentIndex: number;
  isDisabled: boolean;
  setIsDisabled?: (InputState: boolean) => void;
  setShowCompanionForm: (arg0: any) => void;
  clearAttendeeData: () => void;
  attendeeIdentifier: string;
  includedInSelectedIndexes: boolean;
};
const STATUS = {
  SELECTED: 'selected',
  DISABLED: 'disabled',
  SELECTABLE: 'selectable',
  HIDDEN: 'hidden',
};

const IconBlock = ({ status }) => {
  if (status === STATUS.SELECTED) {
    return <Icon name={ICONS.CHECK} width="1.2em" className="text-green" />;
  }

  if (status === STATUS.DISABLED) {
    return (
      <div className="rounded-full border-1 border-mediumGrey p-5 leading-0">
        <Icon className="text-mediumGrey" name={ICONS.PLUS} width="1.2em" />
      </div>
    );
  }

  return (
    <div className="rounded-full border-1 border-saffron bg-saffron p-5 leading-0">
      <Icon className="text-black" name={ICONS.PLUS} width="1.2em" />
    </div>
  );
};

export const TravelCompanionBlock = ({
  travelCompanion,
  isChild,
  position,
  parentIndex,
  isDisabled,
  setIsDisabled,
  setShowCompanionForm,
  clearAttendeeData,
  includedInSelectedIndexes,
  attendeeIdentifier,
}: Props) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isFormDisplayed, setIsFormDisplayed] = useState(false);
  const dispatch = useDispatch();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { trackClickEvent } = useTrackEvent();
  const { companionId, firstName, lastName, birthdate, isSelected } = travelCompanion;
  const isSelectedAndIncludedInSelectedIndexes = isSelected && includedInSelectedIndexes;

  const fillAttendeeWithCompanionData = () =>
    setTravelCompanionField({
      attendeeIdentifier,
      travelCompanion,
      isChild,
      parentIndex,
      values,
      setFieldValue,
    });

  const statusReducer = () => {
    if (isSelectedAndIncludedInSelectedIndexes) {
      // setting attendee infos in form in order to generate an ID for API
      fillAttendeeWithCompanionData();
      return STATUS.SELECTED;
    }

    if (isSelected && !includedInSelectedIndexes) {
      return STATUS.HIDDEN;
    }

    if (isDisabled && !includedInSelectedIndexes) {
      return STATUS.DISABLED;
    }

    return STATUS.SELECTABLE;
  };

  const [status, updateStatus] = useReducer(statusReducer, STATUS.SELECTABLE);
  useEffect(() => {
    updateStatus();
  }, [isSelected, isDisabled]);
  useEffect(() => {
    if (isFormDisplayed) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
        detail_click: 'see_more',
      });
      fillAttendeeWithCompanionData();
      setShowCompanionForm('EDIT');
    } else {
      setShowCompanionForm('');
    }
  }, [isFormDisplayed]);

  const handleSelection = () => {
    if (status !== STATUS.DISABLED) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
        detail_click: isSelectedAndIncludedInSelectedIndexes ? 'remove_companion' : 'add_companion',
      });
      setIsDisabled?.(true);

      if (isSelectedAndIncludedInSelectedIndexes) {
        dispatch(
          unselectTravelCompanion({
            companionId,
            parentIndex,
            position,
          }),
        );
        clearAttendeeData();
        setShowCompanionForm('');
      } else {
        dispatch(
          selectTravelCompanion({
            companionId,
            parentIndex,
            position,
          }),
        );
        fillAttendeeWithCompanionData()?.then((newFormErrors) => {
          const fieldErrorNames = Object.keys(get(newFormErrors, attendeeIdentifier, {}));

          if (fieldErrorNames.length) {
            fieldErrorNames.forEach((fieldErrorName) => {
              setFieldTouched(`${attendeeIdentifier}.${fieldErrorName}`, true);
            });
            setShowCompanionForm('EDIT');
          } else {
            scrollToElement(`#attendee-${parentIndex + 1}`, 500);
          }
        });
      }
    }
  };

  const shouldShowSeeMoreButton =
    (status === STATUS.SELECTABLE || status === STATUS.SELECTED) && !isChild;
  const { thRoot, theNameLabel, theBirthdateLabel } = themes[status]({
    isHovering,
  });
  return (
    <div
      className={classnames(
        'pe-20',
        {
          'order-first': status === STATUS.SELECTED,
        },
        {
          hidden: status === STATUS.HIDDEN,
        },
        {
          'pointer-events-none': isChild,
        },
      )}
      data-testid={`TravelCompanion-${status}`}
    >
      <div
        data-testid="TravelCompanionBlock"
        onClick={handleSelection}
        className={classnames(
          'flex w-full flex-col items-center rounded-12 border-1 border-solid bg-white p-10 text-center shadow-md',
          thRoot,
          'h-140 w-120',
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="mb-15 flex h-30 flex-col items-center justify-end">
          <Icon
            name={ICONS.PEOPLE}
            width={isChild ? '1.375rem' : '1.8125rem'}
            className="text-current"
          />
        </div>
        <p data-testid="TravelCompanionBlockName" className={theNameLabel}>
          {firstName + ' ' + lastName}
        </p>
        <p className={theBirthdateLabel}>
          {birthdate && moment(birthdate).format(t('booking.form.dateFormat'))}
        </p>
        <IconBlock status={status} />
      </div>
      {shouldShowSeeMoreButton && (
        <button
          onClick={() => {
            setIsDisabled?.(true);
            setIsFormDisplayed((hidden) => !hidden);
          }}
          className="mx-auto block py-10 text-14 leading-tight underline"
          type="button"
        >
          {isFormDisplayed ? t('common.loadLess') : t('booking.participants.seeMoreLabel')}
        </button>
      )}
    </div>
  );
};
